import { useState, type FormEvent } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Building2, Loader2, CheckCircle, UserPlus } from "lucide-react";

export function InviteAcceptPage() {
  usePageTitle("Accept Invitation");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();

  const token = searchParams.get("token") ?? "";
  const org = searchParams.get("org") ?? "";
  const role = searchParams.get("role") ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { error: signInError } = await authClient.signIn.email({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message ?? "Failed to sign in");
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch {
      setError("Unable to connect. Please try again.");
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    await authClient.signIn.social({
      provider: "google",
      callbackURL: `${window.location.origin}/invite/accept?token=${encodeURIComponent(token)}&org=${encodeURIComponent(org)}&role=${encodeURIComponent(role)}`,
    });
  }

  async function handleAccept() {
    setError("");
    setLoading(true);

    try {
      const { error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId: token,
      });

      if (acceptError) {
        setError(acceptError.message ?? "Failed to accept invitation");
        setLoading(false);
        return;
      }

      const { data: orgs } = await authClient.organization.list();
      const joinedOrg = orgs?.find((o) => o.name === org);
      setAccepted(true);
      const target = joinedOrg ? `/${joinedOrg.id}/${joinedOrg.slug}/dashboard` : "/onboarding/select-org";
      setTimeout(() => navigate(target, { replace: true }), 1500);
    } catch {
      setError("Unable to connect. Please try again.");
      setLoading(false);
    }
  }

  if (accepted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>You're in!</CardTitle>
          <CardDescription>Redirecting you to the dashboard...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-4">
          <CheckCircle className="h-8 w-8 text-green-500" />
        </CardContent>
      </Card>
    );
  }

  if (sessionPending) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>You've been invited</CardTitle>
        <CardDescription>
          {session ? "Accept the invitation to join" : "Sign in to accept the invitation"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3 rounded-lg border p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{org}</p>
            <p className="text-sm text-muted-foreground">Role: {role}</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {session ? (
          <Button className="w-full" onClick={handleAccept} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )}
            Accept Invitation
          </Button>
        ) : (
          <>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign in & accept
              </Button>
            </form>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                or
              </span>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
            >
              Continue with Google
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
